/**
 * Gemeinsame Verbindungs- und Erneuerungslogik für Socket.IO-Namensräume.
 *
 * Zwei Namensräume (`/sim`, `/chat`) brauchen dasselbe Verhalten: Ein
 * Zugangstoken gilt nur wenige Minuten, eine Sitzung aber stundenlang. Wird
 * die Verbindung deshalb abgewiesen, erneuert der Client das Token und baut
 * die Verbindung neu auf – sonst friert die Oberfläche nach kurzer Zeit
 * stillschweigend ein. Was die beiden Namensräume *tun*, sobald sie verbunden
 * sind (welche Ereignisse sie abonnieren, was sie beim Verbinden nachholen),
 * bleibt bei ihnen selbst – hier steckt nur das Boilerplate drumherum.
 */
import { io, type Socket } from 'socket.io-client';
import { loadStoredAuth, refreshAccessToken } from './api';

export interface AuthedSocket {
  /** Die aktuelle Verbindung, oder `null` vor dem ersten `connect()`. */
  get(): Socket | null;
  /** Baut bei Bedarf eine Verbindung auf und ruft `onReady` bei jedem (Wieder-)Verbinden. */
  connect(onReady: () => void): void;
  disconnect(): void;
  connected(): boolean;
}

export function createAuthedSocket(namespace: string): AuthedSocket {
  let socket: Socket | null = null;
  let reauthAttempts = 0;
  let onReady: (() => void) | null = null;

  function build(token: string): Socket {
    const instance = io(namespace, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
    });

    instance.on('connect', () => {
      reauthAttempts = 0;
      onReady?.();
    });

    // Der Server meldet eine abgelehnte Authentifizierung als `error`-Ereignis
    // und trennt anschließend. Genau hier lohnt sich ein Versuch mit frischem Token.
    instance.on('error', (payload: { message?: string }) => {
      if (payload?.message && /Token/i.test(payload.message)) void reauthenticate();
    });

    instance.on('connect_error', () => {
      if (reauthAttempts === 0) void reauthenticate();
    });

    return instance;
  }

  async function reauthenticate(): Promise<void> {
    if (reauthAttempts >= 3) return;
    reauthAttempts += 1;

    const token = await refreshAccessToken();
    // Ist auch das Refresh-Token abgelaufen, bleibt die Verbindung zu. Die
    // Oberfläche merkt das beim nächsten REST-Aufruf und leitet zur Anmeldung.
    if (!token) return;

    socket?.off();
    socket?.disconnect();
    socket = build(token);
  }

  return {
    get: () => socket,
    connect(handler) {
      onReady = handler;
      const auth = loadStoredAuth();
      if (!auth) return;
      if (!socket) socket = build(auth.accessToken);
      if (socket.connected) handler();
    },
    disconnect() {
      socket?.off();
      socket?.disconnect();
      socket = null;
      reauthAttempts = 0;
      onReady = null;
    },
    connected: () => socket?.connected ?? false,
  };
}
